import type {
  CreateTicketConfigPayload,
  TicketConfigListParams,
  UpdateTicketConfigPayload,
} from '@/shared/api/types/ticket-config-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createTicketConfig,
  getTicketConfigById,
  getTicketConfigs,
  toggleTicketConfigActive,
  updateTicketConfig,
} from '@/shared/api/services/ticket-config-service'

export const TICKET_CONFIG_QUERY_KEYS = {
  all: ['ticket-configs'] as const,
  listPrefix: () => [...TICKET_CONFIG_QUERY_KEYS.all, 'list'] as const,
  listByTenant: (tenantId: string | undefined) =>
    [...TICKET_CONFIG_QUERY_KEYS.all, 'list', tenantId ?? 'tenant-scope'] as const,
  list: (tenantId: string | undefined, params: TicketConfigListParams) =>
    [...TICKET_CONFIG_QUERY_KEYS.listByTenant(tenantId), params] as const,
  detailPrefix: (id: string) => [...TICKET_CONFIG_QUERY_KEYS.all, 'detail', id] as const,
  detail: (id: string, tenantId?: string) =>
    [...TICKET_CONFIG_QUERY_KEYS.all, 'detail', id, tenantId ?? 'tenant-scope'] as const,
}

export function useTicketConfigsQuery(
  tenantId: string | undefined,
  params: TicketConfigListParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: TICKET_CONFIG_QUERY_KEYS.list(tenantId, params),
    queryFn: async () => getTicketConfigs(params, tenantId),
    enabled: options?.enabled ?? tenantId !== '',
    staleTime: 0,
  })
}

export function useTicketConfigByIdQuery(id: string | undefined, tenantId?: string) {
  return useQuery({
    queryKey: TICKET_CONFIG_QUERY_KEYS.detail(id ?? '', tenantId),
    queryFn: async () => getTicketConfigById(id ?? '', tenantId),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useCreateTicketConfigMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      tenantId,
      payload,
    }: {
      tenantId?: string
      payload: CreateTicketConfigPayload
    }) => createTicketConfig(payload, tenantId),
    onSuccess: async (response, { tenantId }) => {
      await queryClient.invalidateQueries({
        queryKey: TICKET_CONFIG_QUERY_KEYS.listByTenant(tenantId),
        refetchType: 'all',
      })
      await queryClient.invalidateQueries({
        queryKey: TICKET_CONFIG_QUERY_KEYS.detailPrefix(response.data.id),
      })
    },
  })
}

export function useUpdateTicketConfigMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      tenantId,
      payload,
    }: {
      id: string
      tenantId?: string
      payload: UpdateTicketConfigPayload
    }) => updateTicketConfig(id, payload, tenantId),
    onSuccess: async (_response, { id }) => {
      await queryClient.invalidateQueries({ queryKey: TICKET_CONFIG_QUERY_KEYS.listPrefix() })
      await queryClient.invalidateQueries({ queryKey: TICKET_CONFIG_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

export function useToggleTicketConfigActiveMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      tenantId,
      isActive,
    }: {
      id: string
      tenantId?: string
      isActive: boolean
    }) => toggleTicketConfigActive(id, isActive, tenantId),
    onSuccess: async (_response, { id }) => {
      await queryClient.invalidateQueries({ queryKey: TICKET_CONFIG_QUERY_KEYS.listPrefix() })
      await queryClient.invalidateQueries({ queryKey: TICKET_CONFIG_QUERY_KEYS.detailPrefix(id) })
    },
  })
}
